/**
 * Cards Page - Virtual Card Management
 * Refactored to use useCardManagement hook for state and handlers
 * NOTE: Physical cards removed - Virtual cards are the exclusive offering
 */
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

// Refactored card components
import { 
  VirtualCardsTab,
  AnalyticsOverviewTab,
  CardBudgetDialog,
  AIRecommendationsDialog,
  NotificationSettingsDialog,
  ScheduledReportsDialog
} from '../components/cards';

// Custom hook for state and handlers
import { useCardManagement } from '../hooks/useCardManagement';

// Real-time alerts
import useWebSocketAlerts from '../hooks/useWebSocketAlerts';

import { 
  CreditCard, 
  Plus, 
  Loader2,
  BarChart3,
  Bell
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Cards = () => {
  const { t } = useTranslation();
  // Use custom hook for all card management state and handlers
  const {
    cards,
    loading,
    creating,
    cardType,
    setCardType,
    showDetails,
    dialogOpen,
    setDialogOpen,
    selectedCard,
    setSelectedCard,
    cardAnalytics,
    cardTransactions,
    overviewAnalytics,
    analyticsLoading,
    cardAlerts,
    allAlerts,
    categoryBudgets,
    setCategoryBudgets,
    alertThreshold,
    setAlertThreshold,
    budgetPeriods,
    setBudgetPeriods,
    budgetStatus,
    budgetDialogOpen,
    setBudgetDialogOpen,
    savingBudgets,
    simulatingSpending,
    exporting,
    aiRecommendations,
    loadingAI,
    applyingRecommendations,
    aiDialogOpen,
    setAiDialogOpen,
    notificationDialogOpen,
    setNotificationDialogOpen,
    notificationPrefs,
    setNotificationPrefs,
    savingNotifications,
    handleCreateCard,
    handleToggleStatus,
    handleDeleteCard,
    handleSimulateSpending,
    handleSaveBudgets,
    handleGetAIRecommendations,
    handleApplyRecommendations,
    handleSaveNotificationPrefs,
    handleSendTestAlert,
    handleExportCSV,
    handleExportPDF,
    toggleShowDetails,
    getAlertColor,
    fetchCardAnalytics,
    fetchAllAlerts,
    fetchOverviewAnalytics
  } = useCardManagement();

  // Real-time WebSocket Alerts
  const { isConnected: wsConnected, lastAlert: wsLastAlert } = useWebSocketAlerts(true);
  
  // Refresh data when a real-time alert is received
  useEffect(() => {
    if (wsLastAlert && selectedCard) {
      fetchCardAnalytics(selectedCard.id);
      fetchAllAlerts();
      fetchOverviewAnalytics();
    }
  }, [wsLastAlert, selectedCard, fetchCardAnalytics, fetchAllAlerts, fetchOverviewAnalytics]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        className="space-y-8" 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        data-testid="cards-container"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-heading font-bold">Virtual Cards</h1>
            </div>
            <p className="text-muted-foreground">AI-powered budgets & spending insights</p>
            <Badge variant="outline" className="mt-2" data-testid="cards-inside-only-note">{t('notices.cardsInsideOnly')}</Badge>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Scheduled Reports */}
            <ScheduledReportsDialog />

            {/* Notification Settings */}
            <NotificationSettingsDialog
              open={notificationDialogOpen}
              onOpenChange={setNotificationDialogOpen}
              notificationPrefs={notificationPrefs}
              setNotificationPrefs={setNotificationPrefs}
              onSave={handleSaveNotificationPrefs}
              saving={savingNotifications}
              onSendTestAlert={handleSendTestAlert}
              hasSelectedCard={!!selectedCard}
            />

            {allAlerts.length > 0 && (
              <div className="relative">
                <Button variant="outline" size="icon" className="rounded-full relative">
                  <Bell className="w-4 h-4" />
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {allAlerts.length}
                  </span>
                </Button>
              </div>
            )}
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full" disabled={cards.length >= 3} data-testid="create-card-btn">
                  <Plus className="w-4 h-4 mr-2" />New Card
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Create Virtual Card</DialogTitle>
                  <DialogDescription>Choose a card type</DialogDescription>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <RadioGroup value={cardType} onValueChange={setCardType} className="space-y-3">
                    <label 
                      htmlFor="standard" 
                      className={`flex items-start space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        cardType === 'standard' ? 'border-emerald-500 bg-emerald-500/5' : 'border-border hover:border-emerald-500/50'
                      }`}
                    >
                      <RadioGroupItem value="standard" id="standard" className="mt-1" />
                      <div className="flex-1">
                        <p className="font-medium">Standard Card</p>
                        <p className="text-sm text-muted-foreground">$1,000 limit</p>
                      </div>
                    </label>
                    <label 
                      htmlFor="premium" 
                      className={`flex items-start space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        cardType === 'premium' ? 'border-amber-500 bg-amber-500/5' : 'border-border hover:border-amber-500/50'
                      }`}
                    >
                      <RadioGroupItem value="premium" id="premium" className="mt-1" />
                      <div className="flex-1">
                        <p className="font-medium flex items-center gap-2">
                          Premium Card 
                          <Badge variant="outline" className="text-amber-500 border-amber-500">Gold</Badge>
                        </p>
                        <p className="text-sm text-muted-foreground">$5,000 limit</p>
                      </div>
                    </label>
                  </RadioGroup>
                  <Button onClick={handleCreateCard} disabled={creating} className="w-full rounded-xl">
                    {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Create Card
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Alerts Banner */}
        <AnimatePresence>
          {allAlerts.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }} 
              animate={{ opacity: 1, height: 'auto' }} 
              exit={{ opacity: 0, height: 0 }}
            >
              <Card className="border-amber-500/20 bg-amber-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Bell className="w-5 h-5 text-amber-500" />
                    <span className="font-medium">Spending Alerts ({allAlerts.length})</span>
                  </div>
                  <div className="space-y-2">
                    {allAlerts.slice(0, 3).map((alert, idx) => (
                      <div 
                        key={`global-alert-${idx}`} 
                        className={`flex items-center gap-3 p-3 rounded-lg border ${getAlertColor(alert.type)}`}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">{alert.title}</p>
                          <p className="text-xs opacity-80">{alert.message}</p>
                        </div>
                        <Badge variant="outline">{alert.percent}%</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {cards.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <CreditCard className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-2">No virtual cards yet</h3>
              <p className="text-muted-foreground mb-4">Create your first card to start</p>
              <Button className="rounded-full" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />Create Card
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="cards" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-12 rounded-xl mb-6">
              <TabsTrigger value="cards" className="rounded-lg">
                <CreditCard className="w-4 h-4 mr-2" />My Cards
              </TabsTrigger>
              <TabsTrigger value="analytics" className="rounded-lg">
                <BarChart3 className="w-4 h-4 mr-2" />Analytics
              </TabsTrigger>
            </TabsList>

            <TabsContent value="cards" className="space-y-6">
              <VirtualCardsTab
                cards={cards}
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
                showDetails={showDetails}
                toggleShowDetails={toggleShowDetails}
                handleToggleStatus={handleToggleStatus}
                handleDeleteCard={handleDeleteCard}
                handleExportCSV={handleExportCSV}
                handleExportPDF={handleExportPDF}
                handleSimulateSpending={handleSimulateSpending}
                handleGetAIRecommendations={handleGetAIRecommendations}
                setBudgetDialogOpen={setBudgetDialogOpen}
                simulatingSpending={simulatingSpending}
                loadingAI={loadingAI}
                exporting={exporting}
                cardAnalytics={cardAnalytics}
                cardTransactions={cardTransactions}
                cardAlerts={cardAlerts}
                analyticsLoading={analyticsLoading}
                alertThreshold={alertThreshold}
                budgetStatus={budgetStatus}
              />
            </TabsContent>

            <TabsContent value="analytics" className="space-y-6">
              <AnalyticsOverviewTab
                overviewAnalytics={overviewAnalytics}
                loading={analyticsLoading && !cardAnalytics}
              />
            </TabsContent>
          </Tabs>
        )}

        {/* Budget Dialog */}
        <CardBudgetDialog
          open={budgetDialogOpen}
          onOpenChange={setBudgetDialogOpen}
          selectedCard={selectedCard}
          categoryBudgets={categoryBudgets}
          setCategoryBudgets={setCategoryBudgets}
          budgetPeriods={budgetPeriods}
          setBudgetPeriods={setBudgetPeriods}
          alertThreshold={alertThreshold}
          setAlertThreshold={setAlertThreshold}
          onSave={handleSaveBudgets}
          saving={savingBudgets}
        />

        {/* AI Recommendations Dialog */}
        <AIRecommendationsDialog
          open={aiDialogOpen}
          onOpenChange={setAiDialogOpen}
          recommendations={aiRecommendations}
          loading={loadingAI}
          applying={applyingRecommendations}
          onApply={handleApplyRecommendations}
          onRefresh={handleGetAIRecommendations}
        />
      </motion.div>
    </Layout>
  );
};

export default Cards;
